import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import { track } from "utils/track";
import { formatLeadDate, getEffectiveStatus } from "layouts/leads/components/leadFormat";

const formatPhone = (phone) => (phone ? phone.replace(/\D/g, "") : "");

// Pedido liberado (locked: false): dados completos e ações de negociação.
function LeadCard({
  lead,
  index,
  isPremium,
  isDummy,
  onAccept,
  onReject,
  onDelete,
  onConvert,
  onOpenContract,
  onUpgrade,
}) {
  const effectiveStatus = getEffectiveStatus(lead);
  const isAccepted = ["accepted", "approved", "confirmado", "hired", "converted"].includes(effectiveStatus);
  const isConfirmed = ["converted", "completed", "hired", "confirmado"].includes(effectiveStatus);

  // Conversão e contratos são Premium: para free, cadeado + modal de upgrade
  const premiumAction = (action) => (isPremium ? action : onUpgrade);
  const lockIcon = !isPremium && <Icon sx={{ mr: 0.5 }}>lock</Icon>;

  return (
    <Card id={index === 0 ? "lead-card-0" : undefined} sx={{ position: "relative", height: "100%" }}>
      <MDBox p={3}>
        {!isDummy && (
          <MDBox
            position="absolute"
            top={8}
            right={8}
            sx={{ cursor: "pointer" }}
            onClick={() => onDelete(lead)}
            role="button"
            aria-label="Excluir pedido"
          >
            <Icon color="error" fontSize="small">delete</Icon>
          </MDBox>
        )}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDBox variant="gradient" bgColor="info" color="white" borderRadius="lg" coloredShadow="info" py={1} px={2}>
            <MDTypography variant="h6" color="white" fontWeight="medium">
              {lead.event_type || lead.eventType || "Evento"}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" flexDirection="column" alignItems="flex-end" id={index === 0 ? "lead-status-0" : undefined}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              {formatLeadDate(lead.event_date || lead.eventDate)}
            </MDTypography>
            <MDBox mt={0.5}>
              <MDBadge
                badgeContent={isConfirmed ? "Confirmado" : "Pendente"}
                color={isConfirmed ? "success" : "warning"}
                variant="gradient"
                size="sm"
              />
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="button" color="text" fontWeight="bold" display="block">
            Cliente: <span style={{ fontWeight: "normal" }}>{lead.client?.name || lead.name || "Não informado"}</span>
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="bold" display="block">
            Local: <span style={{ fontWeight: "normal" }}>{lead.location || lead.city}</span>
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="bold" display="block">
            Convidados: <span style={{ fontWeight: "normal" }}>{lead.guests_count || lead.guestsCount}</span>
          </MDTypography>
        </MDBox>

        <MDBox mb={3} p={2} bgColor="grey-100" borderRadius="lg">
          <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
            Mensagem do Cliente:
          </MDTypography>
          <MDTypography variant="body2" color="text">
            &quot;{lead.message}&quot;
          </MDTypography>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" id={index === 0 ? "lead-actions-0" : undefined}>
          {isAccepted ? (
            <MDBox display="flex" flexDirection="column" gap={1} width="100%">
              <MDButton
                component="a"
                href={`https://wa.me/55${formatPhone(lead.client_phone || lead.phone)}`}
                target="_blank"
                onClick={() => track("dj_contato_cliente", { origem_contato: "leads" })}
                variant="contained"
                color="success"
                fullWidth
                disabled={isDummy}
              >
                <Icon>whatsapp</Icon>&nbsp;Chamar no WhatsApp
              </MDButton>

              {/* Lógica do Contrato baseada no vínculo lead_id */}
              {lead.contract ? (
                <MDButton variant="gradient" color="info" fullWidth onClick={premiumAction(() => onOpenContract(lead))} disabled={isDummy}>
                  {lockIcon || <Icon>description</Icon>}&nbsp;Ver Contrato
                </MDButton>
              ) : lead.event_id ? (
                <MDButton variant="gradient" color="warning" fullWidth onClick={premiumAction(() => onOpenContract(lead))} disabled={isDummy}>
                  {lockIcon || <Icon>assignment</Icon>}&nbsp;Gerar Contrato
                </MDButton>
              ) : (
                <MDButton variant="gradient" color="dark" fullWidth onClick={premiumAction(() => onConvert(lead))}>
                  {lockIcon || <Icon>event_available</Icon>}&nbsp;Confirmar Contratação
                </MDButton>
              )}
            </MDBox>
          ) : (
            <>
              <MDButton variant="outlined" color="error" onClick={() => onReject(lead)} disabled={isDummy}>
                Recusar
              </MDButton>
              <MDButton variant="gradient" color="info" onClick={() => onAccept(lead)} disabled={isDummy}>
                Aceitar
              </MDButton>
            </>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

LeadCard.defaultProps = {
  isDummy: false,
};

LeadCard.propTypes = {
  lead: PropTypes.shape({
    uuid: PropTypes.string,
    event_type: PropTypes.string,
    event_date: PropTypes.string,
    location: PropTypes.string,
    message: PropTypes.string,
    contract: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    event_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  isPremium: PropTypes.bool.isRequired,
  isDummy: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConvert: PropTypes.func.isRequired,
  onOpenContract: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
};

export default LeadCard;
